import React, {useState, useEffect, useMemo} from "react";
import {connect} from "react-redux";
import {AppState, select} from "../../store";
import {AdminNavigationHeader} from "../../atoms/admin-navigation-header";
import {AdminActionRow} from "../../atoms/admin-action-row";
import {DocumentTitle} from "../../atoms/document-title";
import {DashboardGrid} from "../../atoms/dashboard-table";
import {DashboardApproveModal} from "../../atoms/dashboard-table/DashboardApproveModal";
import {DashboardArchiveModal} from "../../atoms/dashboard-table/DashboardArchiveModal";
import {DashboardDeleteAdmissionModal} from "../../atoms/dashboard-table/DashboardDeleteAdmissionModal";
import {DashboardForceDeleteAdmissionModal} from "../../atoms/dashboard-table/DashboardForceDeleteAdmissionModal";
import {DashboardReturnToResidentModal} from "../../atoms/dashboard-table/DashboardReturnToResidentModal";
import {
  ResidentLoginModalProvider,
  ResidentLoginModal,
} from "../../atoms/dashboard-table/atoms/resident-login-modal";
import {DashboardModalContextProvider} from "../../atoms/dashboard-table/DashboardModalContext";
import {PccDashboardCheckboxesContextProvider} from "../../atoms/dashboard-table/atoms/pcc-document-upload-modal";
import {Copyright} from "../../atoms/copyright";
import {
  ChangeHistoryModal,
  ChangeHistoryModalProvider,
} from "../../atoms/dashboard-table/atoms/change-history-modal";
import {DashboardSearchInput} from "../../atoms/dashboard-search";
import {
  DashboardLayout,
  DashboardTileBleed,
  DashboardTileBoardMax,
  FullWidthDashboardHeader,
  FullWidthDashboardLayoutContent,
} from "./styles";
import {useFetchActiveBrandingTheme} from "../page-admin-organizations-branding/hooks/useFetchActiveBrandingTheme";
import {BackdropSpinner} from "../../atoms/spinner";
import {ListTileViewSwitch} from "./ListTileViewSwitch";
import {TILE_VIEW_KEY, VIEW_KEYS} from "./utils/constants";
import {useDashboardSearch} from "./hooks/useDashboardSearch";
import {Status, WorkflowFilter} from "./filters/utils/types";
import {STATUSES} from "./filters/utils/constants";
import {
  ALL_WORKFLOW_FILTER,
  buildWorkflowFilterOptions,
  getDisplayWorkflowFilter,
  getEffectiveWorkflowType,
  getUnionWorkflowTypes,
  shouldShowWorkflowFilter,
} from "./filters/utils/workflowFilterHelpers";
import {DashboardAdmissionsListView} from "./DashboardAdmissionsListView";
import {SimpleFacilityModel} from "../../utils/types/facility";
import {isResideAdmin} from "../../utils/helpers/users";
import {useAdmissionsByStatusQueryUtils} from "../../atoms/dashboard-table/hooks";
import {useStarredAdmissionsCount} from "../../atoms/dashboard-table/hooks/useStarredAdmissionsCount";
import {DashboardFilters} from "./filters/DashboardFilters";

// Context providers wrapper to reduce nesting
const DashboardContextProviders = ({
  isResideAdmin,
  children,
}: {
  isResideAdmin: boolean;
  children: React.ReactNode;
}) => (
  <DashboardModalContextProvider>
    <PccDashboardCheckboxesContextProvider
      isPccDashboardCheckboxesFeatureEnabled={!isResideAdmin}
    >
      <ResidentLoginModalProvider>
        <ChangeHistoryModalProvider>{children}</ChangeHistoryModalProvider>
      </ResidentLoginModalProvider>
    </PccDashboardCheckboxesContextProvider>
  </DashboardModalContextProvider>
);

// Dashboard header component to reduce nesting
const DashboardHeader = ({
  isTileView,
  admissionStatus,
  onStatusSelect,
  onStarFilterChange,
  starFilterActive,
  showWorkflowFilter,
  workflowFilter,
  workflowFilterOptions,
  onWorkflowFilterSelect,
  clearFilters,
  view,
  setView,
  searchInput,
  onSearchInputChange,
}: {
  isTileView: boolean;
  admissionStatus: Status;
  onStatusSelect: (item: Status) => void;
  onStarFilterChange: (isActive: boolean) => void;
  starFilterActive: boolean;
  showWorkflowFilter: boolean;
  workflowFilter: WorkflowFilter;
  workflowFilterOptions: ReadonlyArray<WorkflowFilter>;
  onWorkflowFilterSelect: (item: WorkflowFilter) => void;
  clearFilters: () => void;
  view: (typeof VIEW_KEYS)[number];
  setView: (view: (typeof VIEW_KEYS)[number]) => void;
  searchInput: string;
  onSearchInputChange: (value: string) => void;
}) => (
  <FullWidthDashboardHeader>
    <AdminActionRow
      /* Both views. Each reads the workflow service, which filters, so the term narrows what
         is on screen rather than opening a typeahead that navigates away from it. */
      search={
        <DashboardSearchInput
          value={searchInput}
          onChange={onSearchInputChange}
        />
      }
      filter={
        <DashboardFilters
          onStarFilterChange={onStarFilterChange}
          admissionStatus={admissionStatus}
          onStatusSelect={onStatusSelect}
          starFilterActive={starFilterActive}
          showStarFilter
          showWorkflowFilter={showWorkflowFilter}
          showStatusFilter={!isTileView}
          workflowFilter={workflowFilter}
          workflowFilterOptions={workflowFilterOptions}
          onWorkflowFilterSelect={onWorkflowFilterSelect}
          clearFilters={clearFilters}
        />
      }
      listTileViewSwitch={<ListTileViewSwitch view={view} setView={setView} />}
    />
  </FullWidthDashboardHeader>
);

// Dashboard content component to reduce nesting
const DashboardContent = ({
  isTileView,
  starFilterActive,
  admissionStatus,
  onStatusSelect,
  workflowType,
  searchTerm,
}: {
  isTileView: boolean;
  starFilterActive: boolean;
  admissionStatus: Status;
  onStatusSelect: (item: Status) => void;
  workflowType: string | null;
  searchTerm: string;
}) => (
  <FullWidthDashboardLayoutContent>
    {isTileView ? (
      <DashboardTileBleed>
        <DashboardTileBoardMax>
          <DashboardGrid
            starredOnly={starFilterActive}
            workflowType={workflowType}
            query={searchTerm}
          />
        </DashboardTileBoardMax>
      </DashboardTileBleed>
    ) : (
      <DashboardTileBleed>
        <DashboardTileBoardMax>
          <DashboardAdmissionsListView
            admissionStatus={admissionStatus}
            selectItem={onStatusSelect}
            starFilterActive={starFilterActive}
            workflowType={workflowType}
            query={searchTerm}
          />
        </DashboardTileBoardMax>
      </DashboardTileBleed>
    )}
  </FullWidthDashboardLayoutContent>
);

// Dashboard modals component to group modals
const DashboardModals = () => (
  <>
    <DashboardApproveModal />
    <DashboardArchiveModal />
    <DashboardDeleteAdmissionModal />
    <DashboardForceDeleteAdmissionModal />
    <DashboardReturnToResidentModal />
    <ResidentLoginModal />
    <ChangeHistoryModal />
  </>
);

type StateProps = {
  isResideAdmin: boolean;
  organizationIdByUser: string;
  activeFacilitiesIds: ReadonlyArray<string> | null;
  facilitiesById: Record<string, SimpleFacilityModel | undefined>;
  isAllowedToSeeAdmissions: boolean;
};

const PageAdminDashboardRenderer = ({
  isResideAdmin,
  organizationIdByUser,
  activeFacilitiesIds,
  facilitiesById,
}: StateProps) => {
  // Fetch active custom branding theme
  const {isLoading} = useFetchActiveBrandingTheme({
    organizationIdByUser,
  });

  const [view, setView] = useState<(typeof VIEW_KEYS)[number]>(TILE_VIEW_KEY);
  const [admissionStatus, setAdmissionStatus] = useState(STATUSES[0]);
  const [starFilterActive, setStarFilterActive] = useState(false);
  const [workflowFilter, setWorkflowFilter] = useState(ALL_WORKFLOW_FILTER);

  const showWorkflowFilter = useMemo(
    () => shouldShowWorkflowFilter(activeFacilitiesIds, facilitiesById),
    [activeFacilitiesIds, facilitiesById],
  );

  const scopedWorkflowTypes = useMemo(
    () => getUnionWorkflowTypes(activeFacilitiesIds, facilitiesById),
    [activeFacilitiesIds, facilitiesById],
  );

  const workflowFilterOptions = useMemo(
    () => buildWorkflowFilterOptions(scopedWorkflowTypes),
    [scopedWorkflowTypes],
  );

  const displayWorkflowFilter = useMemo(
    () => getDisplayWorkflowFilter(workflowFilter, workflowFilterOptions),
    [workflowFilter, workflowFilterOptions],
  );

  const effectiveWorkflowType = getEffectiveWorkflowType(
    workflowFilter.id,
    scopedWorkflowTypes,
  );

  const {searchInput, setSearchInput, searchTerm} = useDashboardSearch();

  const {refetchAfterStarToggle} = useAdmissionsByStatusQueryUtils();
  const {data: totalStarredAdmissions} = useStarredAdmissionsCount();

  const isTileView = view === TILE_VIEW_KEY;

  // Reset star filter when count hits 0
  useEffect(() => {
    if (
      starFilterActive &&
      (!totalStarredAdmissions || totalStarredAdmissions === 0)
    ) {
      setStarFilterActive(false);
    }
  }, [starFilterActive, totalStarredAdmissions]);

  // Refetch both views after a star or unstar, so the starred-only filter and count catch up.
  useEffect(() => {
    window.addEventListener("admissionStarToggled", refetchAfterStarToggle);
    return () => {
      window.removeEventListener(
        "admissionStarToggled",
        refetchAfterStarToggle,
      );
    };
  }, [refetchAfterStarToggle]);

  const selectItem = (item: Status) => {
    if (admissionStatus === item) return;
    setAdmissionStatus(item);
  };

  const selectWorkflowFilter = (item: WorkflowFilter) => {
    if (workflowFilter === item) return;
    setWorkflowFilter(item);
  };

  const clearFilters = () => {
    setAdmissionStatus(STATUSES[0]);
    setStarFilterActive(false);
    setWorkflowFilter(ALL_WORKFLOW_FILTER);
  };

  return (
    <BackdropSpinner active={isLoading}>
      <DashboardLayout>
        <DocumentTitle title="Reside - Admin - Dashboard" />
        <AdminNavigationHeader />
        <DashboardContextProviders isResideAdmin={isResideAdmin}>
          <DashboardHeader
            isTileView={isTileView}
            admissionStatus={admissionStatus}
            onStatusSelect={selectItem}
            onStarFilterChange={setStarFilterActive}
            starFilterActive={starFilterActive}
            showWorkflowFilter={showWorkflowFilter}
            workflowFilter={displayWorkflowFilter}
            workflowFilterOptions={workflowFilterOptions}
            onWorkflowFilterSelect={selectWorkflowFilter}
            clearFilters={clearFilters}
            view={view}
            setView={setView}
            searchInput={searchInput}
            onSearchInputChange={setSearchInput}
          />
          <DashboardContent
            isTileView={isTileView}
            starFilterActive={starFilterActive}
            admissionStatus={admissionStatus}
            onStatusSelect={selectItem}
            workflowType={effectiveWorkflowType}
            searchTerm={searchTerm}
          />
          <DashboardModals />
        </DashboardContextProviders>
        <Copyright />
      </DashboardLayout>
    </BackdropSpinner>
  );
};

const mapStateToProps = (
  state: AppState & {adminSession: {user: {organization: {id: string}}}},
): StateProps => {
  const {facilities} = select.adminSession.user(state);
  return {
    isResideAdmin: select.adminSession.isResideAdmin(state),
    organizationIdByUser: state?.adminSession?.user?.organization?.id,
    activeFacilitiesIds: select.adminSession.activeFacilitiesIds(state),
    facilitiesById: select.adminSession.facilitiesById(state),
    isAllowedToSeeAdmissions:
      Boolean(facilities.length) ||
      isResideAdmin(select.adminSession.user(state)),
  };
};

export const PageAdminDashboard = connect(mapStateToProps)(
  PageAdminDashboardRenderer,
);
